import { useEffect, useMemo, useRef, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import "./WalletExperienceStudio.css";

type ExperienceKind = "SCHOOL" | "PERSONAL";
type ReflectionContent = {
  whatIDid: string;
  role: string;
  memorableMoment: string;
  challenge: string;
  action: string;
  learning: string;
  strength: string;
  nextTime: string;
};
type Reflection = { status: "NOT_STARTED" | "DRAFT" | "COMPLETED"; content: Partial<ReflectionContent> };
type EvidenceAttachment = { id: string; objectKey: string; fileName: string; mediaType: string; byteSize: number };
type ExperienceEvidence = {
  evidenceId: string;
  title: string;
  type: string;
  status: string;
  version: number;
  narrative?: string | null;
  externalUrl?: string | null;
  verifiedAt?: string | null;
  attachments: EvidenceAttachment[];
};
type ExperienceSummary = {
  experienceId: string;
  experienceType: ExperienceKind;
  title?: string;
  origin: string;
  participationId?: string | null;
  programTitle?: string | null;
  programType?: string | null;
  activityTitle?: string | null;
  activityType?: string | null;
  institutionName?: string | null;
  status: string;
  attendedAt?: string | null;
  completedAt?: string | null;
  sessionStartsAt?: string | null;
  sessionEndsAt?: string | null;
  startedOn?: string | null;
  endedOn?: string | null;
  locationName?: string | null;
  schoolYear?: number | null;
  gradeLevel?: string | null;
  classLabel?: string | null;
  reflectionStatus?: string;
  evidence?: Array<{ evidenceId: string; title: string; status: string }>;
  evidenceCount?: number;
  competencyCount?: number;
  achievementCount?: number;
  lifeStampCount?: number;
  reviewFeedback?: string | null;
  version?: number;
  experienceCategory?: string;
  organizationOrPlace?: string | null;
  description?: string | null;
  role?: string | null;
};
type ExperienceDetail = {
  experience: ExperienceSummary & { learnerId?: string };
  officialRecord: Record<string, unknown> | null;
  personalRecord: Record<string, unknown> | null;
  reflection: Reflection;
  evidence: ExperienceEvidence[];
  competencies: Array<{ competencyId: string; name: string; indicator?: string | null; score: number; observedAt: string }>;
  achievements: Array<{ achievementId: string; name: string; description?: string | null; confirmedAt?: string | null }>;
  lifeStamps: Array<{ credentialId: string; credentialName: string; achievementName: string; issuerName: string; issuedAt: string; status: string }>;
};
type PersonalDraft = {
  title: string;
  experienceType: string;
  organizationOrPlace: string;
  startedOn: string;
  endedOn: string;
  description: string;
  role: string;
};
type ListPayload = { experiences: ExperienceSummary[] };

const PERSONAL_TYPES = [
  ["PERSONAL_PROJECT", "개인 프로젝트"], ["READING", "독서"], ["VOLUNTEER", "봉사"],
  ["COMPETITION", "대회"], ["ONLINE_LEARNING", "온라인 학습"], ["JOB_SHADOWING", "직업체험"],
  ["CREATIVE_ACTIVITY", "창작활동"], ["CERTIFICATE_COURSE", "자격과정"], ["TRIP", "여행·탐방"],
  ["CLUB_ACTIVITY", "동아리 외 활동"], ["OTHER", "기타"]
];
const FILE_TYPES: Record<string, string> = {
  "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp", "application/pdf": "pdf",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/vnd.ms-powerpoint": "ppt",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "pptx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx"
};
const FILE_ACCEPT = Object.keys(FILE_TYPES).join(",");
const EMPTY_REFLECTION: ReflectionContent = {
  whatIDid: "", role: "", memorableMoment: "", challenge: "", action: "", learning: "", strength: "", nextTime: ""
};

function dateText(value?: string | null) {
  if (!value) return "날짜 정보 없음";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "날짜 정보 없음" : date.toLocaleDateString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit" });
}

function experienceStatus(value: string) {
  const labels: Record<string, string> = {
    ATTENDED: "참여 확인", COMPLETED: "이수 확인", SELF_REPORTED: "학생 자기기록", SUBMITTED: "교사 확인 요청",
    VERIFIED: "교사 확인 완료", NEEDS_REVISION: "보완 요청", REJECTED: "재검토 필요"
  };
  return labels[value] || value;
}

function reflectionStatus(value?: string) {
  const labels: Record<string, string> = { NOT_STARTED: "아직 기록 전", DRAFT: "작성 중", COMPLETED: "기록 완료" };
  return labels[value || "NOT_STARTED"] || "아직 기록 전";
}

function dateRange(item: ExperienceSummary) {
  const start = item.startedOn || item.attendedAt || item.sessionStartsAt;
  const end = item.endedOn || item.completedAt || item.sessionEndsAt;
  if (!start) return "날짜 정보 없음";
  return end && end.slice(0, 10) !== start.slice(0, 10) ? `${dateText(start)} ~ ${dateText(end)}` : dateText(start);
}

function detailPath(item: ExperienceSummary) {
  return `/experiences/${item.experienceType.toLowerCase()}/${item.experienceId}`;
}

function errorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

function experienceTitle(item: ExperienceSummary) {
  return item.activityTitle || item.title || "나의 경험";
}

function isVerified(item: ExperienceSummary) {
  return item.experienceType === "SCHOOL" || item.status === "VERIFIED";
}

export function WalletExperienceStudioPage({
  client,
  experienceId,
  experienceType,
  onNavigate,
  onUseInPortfolio
}: {
  client: PlatformClient;
  experienceId?: string;
  experienceType?: ExperienceKind;
  onNavigate: (path: string) => void;
  onUseInPortfolio: (sourceType: "EXPERIENCE", sourceId: string) => void;
}) {
  if (experienceId && experienceType) {
    return <ExperienceDetailPage key={`${experienceType}:${experienceId}`} client={client} experienceId={experienceId} experienceType={experienceType} onNavigate={onNavigate} onUseInPortfolio={onUseInPortfolio} />;
  }
  return <ExperienceListPage client={client} onNavigate={onNavigate} />;
}

function ExperienceListPage({ client, onNavigate }: { client: PlatformClient; onNavigate: (path: string) => void }) {
  const [items, setItems] = useState<ExperienceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("ALL");
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    let active = true;
    Promise.resolve(client.rpc("wallet_experience_studio")).then(({ data, error: rpcError }) => {
      if (!active) return;
      if (rpcError) setError(rpcError.message);
      else setItems(((data as ListPayload | null)?.experiences || []) as ExperienceSummary[]);
      setLoading(false);
    }).catch((reason) => {
      if (active) { setError(errorMessage(reason, "경험을 불러오지 못했습니다.")); setLoading(false); }
    });
    return () => { active = false; };
  }, [client]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("ko-KR");
    return items.filter((item) => {
      const haystack = [experienceTitle(item), item.programTitle, item.institutionName, item.organizationOrPlace]
        .filter(Boolean).join(" ").toLocaleLowerCase("ko-KR");
      if (needle && !haystack.includes(needle)) return false;
      if (filter === "SCHOOL") return item.experienceType === "SCHOOL";
      if (filter === "PERSONAL") return item.experienceType === "PERSONAL";
      if (filter === "VERIFIED") return isVerified(item);
      if (filter === "SELF_REPORTED") return item.experienceType === "PERSONAL" && item.status !== "VERIFIED";
      if (filter === "STAMP") return (item.lifeStampCount || 0) > 0;
      if (filter === "EVIDENCE") return (item.evidenceCount ?? item.evidence?.length ?? 0) > 0;
      return true;
    });
  }, [items, query, filter]);

  const groups = useMemo(() => {
    const map = new Map<number | string, ExperienceSummary[]>();
    filtered.forEach((item) => {
      const rawDate = item.startedOn || item.completedAt || item.attendedAt || item.sessionStartsAt;
      const year = item.schoolYear || (rawDate ? new Date(rawDate).getFullYear() : "연도 미상");
      map.set(year, [...(map.get(year) || []), item]);
    });
    return [...map.entries()].sort(([a], [b]) => Number(b) - Number(a));
  }, [filtered]);

  return (
    <section className="wallet-page wallet-experience-studio">
      <div className="section-heading experience-list-heading">
        <div>
          <p className="eyebrow">LIFE PASSPORT · EXPERIENCE</p>
          <h2>나의 경험</h2>
          <p className="route-description">학교 기록과 내가 남긴 경험을 살펴보고, 나의 말과 증거로 성장 과정을 채워보세요.</p>
        </div>
        <button className="primary" type="button" onClick={() => setShowCreate(true)}>내 경험 추가하기</button>
      </div>

      <div className="experience-list-tools">
        <label className="experience-search">
          <span>경험 검색</span>
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="활동명, 프로그램, 기관 검색" />
        </label>
        <div className="experience-filter-row" aria-label="경험 필터">
          {[["ALL", "전체"], ["SCHOOL", "학교활동"], ["PERSONAL", "개인경험"], ["VERIFIED", "확인완료"], ["SELF_REPORTED", "자기기록"], ["STAMP", "Life Stamp 연결"], ["EVIDENCE", "증빙 있음"]].map(([key, label]) => (
            <button key={key} type="button" className={filter === key ? "active" : ""} onClick={() => setFilter(key)}>{label}</button>
          ))}
        </div>
      </div>

      {error && <p className="error" role="alert">{error}</p>}
      {loading ? <p className="muted">경험을 불러오는 중…</p> : groups.length ? groups.map(([year, rows]) => (
        <section className="experience-year-group" key={String(year)} aria-labelledby={`experience-year-${year}`}>
          <div className="experience-year-heading"><h3 id={`experience-year-${year}`}>{year}{typeof year === "number" ? "년" : ""}</h3><span>{rows.length}개 기록</span></div>
          <div className="experience-card-grid">
            {rows.map((item) => {
              const verified = isVerified(item);
              const evidenceCount = item.evidenceCount ?? item.evidence?.length ?? 0;
              return (
                <article className="experience-summary-card" key={`${item.experienceType}:${item.experienceId}`}>
                  <div className="experience-card-topline">
                    <span className={`experience-origin-chip ${item.experienceType.toLowerCase()}`}>{item.experienceType === "SCHOOL" ? "학교 기록" : "나의 기록"}</span>
                    <span className={`experience-status ${verified ? "verified" : "self"}`}>{experienceStatus(item.status)}</span>
                  </div>
                  <h4>{experienceTitle(item)}</h4>
                  <p className="experience-institution">{item.programTitle || item.organizationOrPlace || item.institutionName || "기관·장소 정보 없음"}</p>
                  <p className="experience-dates">{dateRange(item)}{item.gradeLevel ? ` · ${item.gradeLevel}` : ""}</p>
                  <div className="experience-metric-row">
                    <span>증빙 <b>{evidenceCount}</b></span>
                    <span>성찰 <b>{reflectionStatus(item.reflectionStatus)}</b></span>
                    <span>역량 <b>{item.competencyCount || 0}</b></span>
                    <span>성취 <b>{item.achievementCount || 0}</b></span>
                    <span>Stamp <b>{item.lifeStampCount || 0}</b></span>
                  </div>
                  {item.reviewFeedback && <p className="experience-feedback-preview">보완 안내: {item.reviewFeedback}</p>}
                  <button className="secondary experience-open-button" type="button" onClick={() => onNavigate(detailPath(item))}>경험 열기 <span aria-hidden="true">›</span></button>
                </article>
              );
            })}
          </div>
        </section>
      )) : (
        <div className="experience-empty-state">
          <span aria-hidden="true">◇</span>
          <h3>{items.length ? "검색 조건에 맞는 경험이 없습니다" : "아직 기록된 경험이 없습니다"}</h3>
          <p>{items.length ? "검색어와 필터를 바꾸어 다시 살펴보세요." : "학교 경험을 기다리는 동안, 학교 밖에서 한 일도 내 경험으로 남길 수 있어요."}</p>
          {!items.length && <button className="primary" type="button" onClick={() => setShowCreate(true)}>내 경험 추가하기</button>}
        </div>
      )}

      {showCreate && <CreateExperienceDialog client={client} onClose={() => setShowCreate(false)} onCreated={(id) => onNavigate(`/experiences/personal/${id}`)} />}
    </section>
  );
}

function CreateExperienceDialog({ client, onClose, onCreated }: { client: PlatformClient; onClose: () => void; onCreated: (id: string) => void }) {
  const [draft, setDraft] = useState<PersonalDraft>({ title: "", experienceType: "PERSONAL_PROJECT", organizationOrPlace: "", startedOn: "", endedOn: "", description: "", role: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function create() {
    if (!draft.title.trim() || !draft.startedOn) { setError("활동명과 활동일을 입력해 주세요."); return; }
    setBusy(true); setError("");
    const { data, error: rpcError } = await client.rpc("wallet_create_personal_experience", {
      p_title: draft.title, p_experience_type: draft.experienceType,
      p_organization_or_place: draft.organizationOrPlace || null, p_started_on: draft.startedOn,
      p_ended_on: draft.endedOn || null, p_description: draft.description || null, p_role: draft.role || null
    });
    setBusy(false);
    if (rpcError) { setError(rpcError.message); return; }
    onCreated(String(data));
  }
  return (
    <div className="experience-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="experience-modal" role="dialog" aria-modal="true" aria-labelledby="experience-create-title">
        <div className="experience-modal-heading"><div><p className="eyebrow">나의 성장기록</p><h3 id="experience-create-title">내 경험 추가하기</h3></div><button className="icon-button" type="button" aria-label="닫기" onClick={onClose}>×</button></div>
        <p className="muted">이 기록은 학생 자기기록으로 저장됩니다. 교사가 확인하기 전까지 공식 확인으로 표시되지 않습니다.</p>
        <label>활동명 *<input autoFocus maxLength={160} value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="예: 동네 생태지도 만들기" /></label>
        <label>활동유형 *<select value={draft.experienceType} onChange={(e) => setDraft({ ...draft, experienceType: e.target.value })}>{PERSONAL_TYPES.map(([key, label]) => <option value={key} key={key}>{label}</option>)}</select></label>
        <label>기관 또는 장소<input maxLength={160} value={draft.organizationOrPlace} onChange={(e) => setDraft({ ...draft, organizationOrPlace: e.target.value })} placeholder="선택 입력" /></label>
        <div className="experience-form-date-grid"><label>시작일 *<input type="date" value={draft.startedOn} onChange={(e) => setDraft({ ...draft, startedOn: e.target.value })} /></label><label>종료일<input type="date" value={draft.endedOn} onChange={(e) => setDraft({ ...draft, endedOn: e.target.value })} /></label></div>
        <label>활동 설명<textarea rows={3} maxLength={3000} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder="무엇을 해본 경험인가요?" /></label>
        <label>나의 역할<textarea rows={2} maxLength={1000} value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })} placeholder="내가 맡은 일을 적어보세요." /></label>
        {error && <p className="error" role="alert">{error}</p>}
        <div className="experience-modal-actions"><button className="secondary" type="button" onClick={onClose}>취소</button><button className="primary" type="button" disabled={busy} onClick={() => void create()}>{busy ? "저장 중…" : "경험 저장"}</button></div>
      </section>
    </div>
  );
}

function ExperienceDetailPage({ client, experienceId, experienceType, onNavigate, onUseInPortfolio }: {
  client: PlatformClient;
  experienceId: string;
  experienceType: ExperienceKind;
  onNavigate: (path: string) => void;
  onUseInPortfolio: (sourceType: "EXPERIENCE", sourceId: string) => void;
}) {
  const [detail, setDetail] = useState<ExperienceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [showMoreReflection, setShowMoreReflection] = useState(false);
  const [reflection, setReflection] = useState<ReflectionContent>(EMPTY_REFLECTION);
  const [reflectionSaved, setReflectionSaved] = useState("NOT_STARTED");
  const [editingExperience, setEditingExperience] = useState(false);
  const [personalDraft, setPersonalDraft] = useState<PersonalDraft | null>(null);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkTitle, setLinkTitle] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkDescription, setLinkDescription] = useState("");
  const [evidenceFile, setEvidenceFile] = useState<File | null>(null);
  const [evidenceTitle, setEvidenceTitle] = useState("");
  const [evidenceDescription, setEvidenceDescription] = useState("");
  const [editingEvidenceId, setEditingEvidenceId] = useState<string | null>(null);
  const [editingEvidenceTitle, setEditingEvidenceTitle] = useState("");
  const [editingEvidenceDescription, setEditingEvidenceDescription] = useState("");
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let active = true;
    setLoading(true); setError("");
    Promise.resolve(client.rpc("wallet_experience_detail", { p_experience_type: experienceType, p_experience_id: experienceId })).then(({ data, error: rpcError }) => {
      if (!active) return;
      if (rpcError) setError(rpcError.message);
      else {
        const result = data as ExperienceDetail;
        setDetail(result);
        setReflection({ ...EMPTY_REFLECTION, ...(result.reflection?.content || {}) });
        setReflectionSaved(result.reflection?.status || "NOT_STARTED");
        if (result.experience.experienceType === "PERSONAL") {
          setPersonalDraft({
            title: result.experience.title || "", experienceType: result.experience.experienceCategory || "OTHER",
            organizationOrPlace: result.experience.organizationOrPlace || "", startedOn: (result.experience.startedOn || "").slice(0, 10),
            endedOn: (result.experience.endedOn || "").slice(0, 10), description: result.experience.description || "", role: result.experience.role || ""
          });
        }
      }
      setLoading(false);
    }).catch((reason) => { if (active) { setError(errorMessage(reason, "경험을 불러오지 못했습니다.")); setLoading(false); } });
    return () => { active = false; };
  }, [client, experienceId, experienceType, refresh]);

  async function run(key: string, action: () => PromiseLike<{ data?: unknown; error?: { message: string } | null }>, success?: string) {
    setBusy(key); setError(""); setNotice("");
    try {
      const result = await action();
      if (result.error) throw new Error(result.error.message);
      if (success) setNotice(success);
      setRefresh((current) => current + 1);
      return result.data;
    } catch (reason) {
      setError(errorMessage(reason, "저장하지 못했습니다. 다시 시도해 주세요."));
      return null;
    } finally { setBusy(""); }
  }

  async function saveReflection(status: "DRAFT" | "COMPLETED") {
    if (!reflection.whatIDid.trim() && !reflection.learning.trim()) { setError("내가 한 일 또는 배운 점을 한 가지 이상 적어주세요."); return; }
    const result = await run("reflection", () => client.rpc("wallet_save_experience_reflection", {
      p_experience_type: experienceType, p_experience_id: experienceId, p_content: reflection, p_status: status
    }), status === "COMPLETED" ? "나의 기록을 완료했습니다." : "나의 기록을 저장했습니다.");
    if (result) setReflectionSaved(status);
  }

  async function savePersonalExperience() {
    if (!personalDraft || !detail) return;
    await run("personal", () => client.rpc("wallet_update_personal_experience", {
      p_experience_id: experienceId, p_expected_version: detail.experience.version,
      p_title: personalDraft.title, p_experience_type: personalDraft.experienceType,
      p_organization_or_place: personalDraft.organizationOrPlace || null,
      p_started_on: personalDraft.startedOn, p_ended_on: personalDraft.endedOn || null,
      p_description: personalDraft.description || null, p_role: personalDraft.role || null
    }), "경험 정보를 저장했습니다.");
    setEditingExperience(false);
  }

  async function submitPersonalExperience() {
    if (!detail) return;
    if (!window.confirm("이 경험을 교사 확인 요청으로 제출할까요? 제출한 뒤에는 경험 정보를 수정할 수 없습니다.")) return;
    await run("submit", () => client.rpc("wallet_submit_personal_experience", { p_experience_id: experienceId, p_expected_version: detail.experience.version }), "교사 확인을 요청했습니다.");
  }

  async function archivePersonalExperience() {
    if (!detail || !window.confirm("이 경험을 보관할까요? 보관한 기록은 목록에서 숨겨집니다.")) return;
    const result = await run("archive", () => client.rpc("wallet_archive_personal_experience", { p_experience_id: experienceId, p_expected_version: detail.experience.version }));
    if (result) onNavigate("/experiences");
  }

  function selectEvidenceFile(file?: File) {
    if (!file) return;
    const extension = FILE_TYPES[file.type];
    if (!extension) { setError("이미지, PDF, Word, PowerPoint, Excel 파일만 추가할 수 있습니다."); return; }
    if (file.size < 1 || file.size > 10 * 1024 * 1024) { setError("파일은 10MB 이하로 추가해 주세요."); return; }
    setEvidenceFile(file);
    setEvidenceTitle(file.name.slice(0, 240));
    setEvidenceDescription("");
    setError("");
  }

  async function addFile() {
    const file = evidenceFile;
    if (!file || !detail) return;
    const extension = FILE_TYPES[file.type];
    const attachmentId = crypto.randomUUID();
    const objectKey = `wallet-evidence/${detail.experience.learnerId}/${experienceId}/${attachmentId}.${extension}`;
    setBusy("upload"); setError(""); setNotice("");
    let uploaded = false;
    try {
      const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
      const hash = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
      const upload = await client.storage.from("rs-career-artifacts").upload(objectKey, file, { upsert: false, contentType: file.type });
      if (upload.error) throw new Error(upload.error.message);
      uploaded = true;
      const created = await client.rpc("wallet_add_experience_evidence", {
        p_experience_type: experienceType, p_experience_id: experienceId, p_attachment_id: attachmentId,
        p_title: evidenceTitle.trim() || file.name.slice(0, 240), p_description: evidenceDescription.trim() || null, p_object_key: objectKey, p_file_name: file.name,
        p_media_type: file.type, p_byte_size: file.size, p_sha256_hex: hash
      });
      if (created.error) throw new Error(created.error.message);
      setNotice("증빙을 추가했습니다. 교사에게 보여주려면 확인 요청을 눌러주세요.");
      setEvidenceFile(null); setEvidenceTitle(""); setEvidenceDescription("");
      setRefresh((current) => current + 1);
    } catch (reason) {
      if (uploaded) await client.storage.from("rs-career-artifacts").remove([objectKey]);
      setError(errorMessage(reason, "파일을 추가하지 못했습니다. 작성 중인 기록은 유지됩니다."));
    } finally {
      setBusy("");
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  async function addLink() {
    if (!linkTitle.trim() || !linkUrl.trim()) { setError("링크 제목과 주소를 입력해 주세요."); return; }
    try { if (new URL(linkUrl).protocol !== "https:") throw new Error("https 링크만 추가할 수 있습니다."); }
    catch (reason) { setError(errorMessage(reason, "유효한 https 링크를 입력해 주세요.")); return; }
    const result = await run("link", () => client.rpc("wallet_add_experience_link", {
      p_experience_type: experienceType, p_experience_id: experienceId,
      p_title: linkTitle.trim(), p_url: linkUrl.trim(), p_description: linkDescription.trim() || null
    }), "링크 증빙을 추가했습니다.");
    if (result) { setLinkTitle(""); setLinkUrl(""); setLinkDescription(""); setShowLinkForm(false); }
  }

  async function submitEvidence(item: ExperienceEvidence) {
    await run(`evidence:${item.evidenceId}`, () => client.rpc("wallet_submit_experience_evidence", { p_evidence_id: item.evidenceId, p_expected_version: item.version }), "증빙 확인을 요청했습니다.");
  }

  function startEvidenceEdit(item: ExperienceEvidence) {
    setEditingEvidenceId(item.evidenceId);
    setEditingEvidenceTitle(item.title);
    setEditingEvidenceDescription(item.narrative || "");
  }

  async function saveEvidenceEdit(item: ExperienceEvidence) {
    const result = await run(`evidence-edit:${item.evidenceId}`, () => client.rpc("wallet_update_experience_evidence", {
      p_evidence_id: item.evidenceId,
      p_expected_version: item.version,
      p_title: editingEvidenceTitle.trim(),
      p_description: editingEvidenceDescription.trim() || null
    }), "증빙 제목과 설명을 수정했습니다.");
    if (result !== null) setEditingEvidenceId(null);
  }

  async function openAttachment(attachment: EvidenceAttachment) {
    const { data, error: storageError } = await client.storage.from("rs-career-artifacts").createSignedUrl(attachment.objectKey, 120);
    if (storageError || !data?.signedUrl) { setError(storageError?.message || "증빙을 열지 못했습니다."); return; }
    window.open(data.signedUrl, "_blank", "noopener,noreferrer");
  }

  if (loading) return <section className="wallet-page"><p className="muted">경험을 불러오는 중…</p></section>;
  if (!detail) return <section className="wallet-page"><button className="secondary" type="button" onClick={() => onNavigate("/experiences")}>‹ 경험 목록</button>{error && <p className="error" role="alert">{error}</p>}</section>;

  const item = detail.experience;
  const personal = experienceType === "PERSONAL";
  const canEditPersonal = personal && ["SELF_REPORTED", "NEEDS_REVISION", "REJECTED"].includes(item.status);
  const date = dateRange(item);
  const reflectionPrompts: Array<[keyof ReflectionContent, string, string]> = [
    ["role", "나의 역할", "팀 또는 활동에서 어떤 역할을 맡았나요?"],
    ["memorableMoment", "기억에 남는 순간", "가장 기억에 남는 순간은 무엇인가요?"],
    ["challenge", "어려웠던 점", "어떤 부분이 어려웠나요?"],
    ["action", "내가 한 선택", "어려움 속에서 어떤 행동을 했나요?"],
    ["strength", "나의 강점", "이 활동에서 나의 어떤 강점이 나타났나요?"],
    ["nextTime", "다음에 다시 한다면", "다음에는 무엇을 다르게 해보고 싶나요?"]
  ];

  return (
    <section className="wallet-page wallet-experience-studio">
      <button className="experience-back-link" type="button" onClick={() => onNavigate("/experiences")}>‹ 나의 경험</button>
      <header className="experience-detail-heading">
        <div className="experience-card-topline"><span className={`experience-origin-chip ${personal ? "personal" : "school"}`}>{personal ? "나의 기록" : "학교 기록"}</span><span className={`experience-status ${isVerified(item) ? "verified" : "self"}`}>{experienceStatus(item.status)}</span></div>
        <h1>{experienceTitle(item)}</h1>
        <p>{personal ? (item.organizationOrPlace || "기관·장소 정보 없음") : (item.institutionName || item.programTitle || "학교 활동")}</p>
        <div className="experience-detail-meta"><span>{date}</span>{item.gradeLevel && <span>{item.gradeLevel}{item.classLabel ? ` · ${item.classLabel}` : ""}</span>}{detail.officialRecord?.activityType ? <span>{String(detail.officialRecord.activityType)}</span> : null}</div>
        <div className="experience-detail-actions"><button className="secondary" type="button" onClick={() => onUseInPortfolio("EXPERIENCE", experienceId)}>문서에 활용</button>{canEditPersonal && <button className="secondary" type="button" onClick={() => setEditingExperience((value) => !value)}>{editingExperience ? "편집 닫기" : "경험 정보 수정"}</button>}</div>
      </header>

      {error && <p className="error" role="alert">{error}</p>}{notice && <p className="success-note" role="status">{notice}</p>}

      {personal ? (
        <section className="experience-record-zone self-record-zone">
          <div className="experience-zone-heading"><div><p className="eyebrow">SELF-REPORTED</p><h2>내가 남긴 경험</h2></div><span className="experience-trust-note">학생 자기기록</span></div>
          {item.reviewFeedback && <div className="experience-review-feedback"><strong>교사가 남긴 안내</strong><p>{item.reviewFeedback}</p></div>}
          {editingExperience && personalDraft && (
            <div className="experience-edit-form">
              <label>활동명 *<input value={personalDraft.title} onChange={(e) => setPersonalDraft({ ...personalDraft, title: e.target.value })} /></label>
              <label>활동유형 *<select value={personalDraft.experienceType} onChange={(e) => setPersonalDraft({ ...personalDraft, experienceType: e.target.value })}>{PERSONAL_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label>
              <label>기관 또는 장소<input value={personalDraft.organizationOrPlace} onChange={(e) => setPersonalDraft({ ...personalDraft, organizationOrPlace: e.target.value })} /></label>
              <div className="experience-form-date-grid"><label>시작일 *<input type="date" value={personalDraft.startedOn} onChange={(e) => setPersonalDraft({ ...personalDraft, startedOn: e.target.value })} /></label><label>종료일<input type="date" value={personalDraft.endedOn} onChange={(e) => setPersonalDraft({ ...personalDraft, endedOn: e.target.value })} /></label></div>
              <label>활동 설명<textarea rows={3} value={personalDraft.description} onChange={(e) => setPersonalDraft({ ...personalDraft, description: e.target.value })} /></label>
              <label>나의 역할<textarea rows={2} value={personalDraft.role} onChange={(e) => setPersonalDraft({ ...personalDraft, role: e.target.value })} /></label>
              <button className="primary" type="button" disabled={busy === "personal"} onClick={() => void savePersonalExperience()}>{busy === "personal" ? "저장 중…" : "경험 정보 저장"}</button>
            </div>
          )}
          {!editingExperience && <div className="experience-official-facts"><Fact label="활동유형" value={PERSONAL_TYPES.find(([key]) => key === item.experienceCategory)?.[1] || item.experienceCategory || "정보 없음"} /><Fact label="활동 설명" value={item.description || "아직 설명을 적지 않았습니다."} /><Fact label="나의 역할" value={item.role || "아직 역할을 적지 않았습니다."} /></div>}
          {canEditPersonal && <div className="experience-review-request-actions"><button className="primary" type="button" disabled={busy === "submit"} onClick={() => void submitPersonalExperience()}>{busy === "submit" ? "제출 중…" : "교사 확인 요청"}</button><button className="quiet-button" type="button" disabled={busy === "archive"} onClick={() => void archivePersonalExperience()}>보관하기</button></div>}
          {item.status === "SUBMITTED" && <p className="muted">교사 확인을 기다리고 있습니다. 이 요청이 승인되기 전까지 학교 공식기록으로 표시되지 않습니다.</p>}
        </section>
      ) : (
        <section className="experience-record-zone official-record-zone">
          <div className="experience-zone-heading"><div><p className="eyebrow">OFFICIAL RECORD</p><h2>확인된 기록</h2></div><span className="experience-trust-note official">학교 기록 · 수정할 수 없음</span></div>
          <div className="experience-official-facts">
            <Fact label="프로그램" value={String(detail.officialRecord?.programTitle || "기록된 프로그램 정보 없음")} />
            <Fact label="활동" value={String(detail.officialRecord?.activityTitle || item.activityTitle || "기록된 활동 정보 없음")} />
            <Fact label="주관기관" value={String(detail.officialRecord?.schoolName || "기관 정보 없음")} />
            <Fact label="참여상태" value={experienceStatus(String(detail.officialRecord?.participationStatus || item.status))} />
            <Fact label="기간" value={date} />
            <Fact label="장소" value={String(detail.officialRecord?.locationName || "기록된 장소 정보 없음")} />
            <Fact label="교사 확인 증빙" value={detail.evidence.filter((evidence) => evidence.status === "VERIFIED").map((evidence) => evidence.title).join(", ") || "확인 완료된 증빙 없음"} />
          </div>
        </section>
      )}

      <section className="experience-record-zone reflection-zone">
        <div className="experience-zone-heading"><div><p className="eyebrow">STUDENT REFLECTION</p><h2>나의 기록</h2><p>내가 기억하고 이해한 경험을 학생의 말로 적습니다.</p></div><span className={`reflection-state ${reflectionSaved.toLowerCase()}`}>{reflectionStatus(reflectionSaved)}</span></div>
        <label className="experience-prompt-field"><span>내가 한 일</span><small>이 활동에서 실제로 무엇을 했나요?</small><textarea rows={5} maxLength={3000} value={reflection.whatIDid} onChange={(e) => setReflection({ ...reflection, whatIDid: e.target.value })} placeholder="내가 맡은 일과 직접 한 행동을 적어보세요." /></label>
        <label className="experience-prompt-field"><span>배운 점</span><small>이 경험을 통해 무엇을 새롭게 알게 되었나요?</small><textarea rows={5} maxLength={3000} value={reflection.learning} onChange={(e) => setReflection({ ...reflection, learning: e.target.value })} placeholder="새롭게 알게 된 점이나 달라진 생각을 적어보세요." /></label>
        <button className="experience-more-toggle" type="button" aria-expanded={showMoreReflection} onClick={() => setShowMoreReflection((value) => !value)}>{showMoreReflection ? "선택 질문 접기" : "더 기록하기"}<span aria-hidden="true">{showMoreReflection ? "−" : "+"}</span></button>
        {showMoreReflection && <div className="experience-optional-prompts">{reflectionPrompts.map(([key, title, prompt]) => <label className="experience-prompt-field" key={key}><span>{title}</span><small>{prompt}</small><textarea rows={3} maxLength={3000} value={reflection[key]} onChange={(e) => setReflection({ ...reflection, [key]: e.target.value })} placeholder="선택해서 기록해도 괜찮아요." /></label>)}</div>}
        <div className="experience-sticky-actions"><span>{reflectionSaved === "COMPLETED" ? "나의 기록 완료" : "페이지를 나가기 전에 임시 저장해 주세요."}</span><div><button className="secondary" type="button" disabled={busy === "reflection"} onClick={() => void saveReflection("DRAFT")}>임시 저장</button><button className="primary" type="button" disabled={busy === "reflection"} onClick={() => void saveReflection("COMPLETED")}>기록 완료</button></div></div>
      </section>

      <section className="experience-record-zone evidence-zone">
        <div className="experience-zone-heading"><div><p className="eyebrow">PRIVATE EVIDENCE</p><h2>증빙</h2><p>증빙 파일은 비공개로 보관됩니다. 교사 확인 전에는 확인 완료로 표시되지 않습니다.</p></div><div className="experience-evidence-actions"><input ref={fileInput} className="visually-hidden" type="file" accept={`image/*,application/pdf,${FILE_ACCEPT}`} onChange={(event) => selectEvidenceFile(event.target.files?.[0])} /><button className="secondary" type="button" disabled={busy === "upload"} onClick={() => fileInput.current?.click()}>파일 선택</button><button className="secondary" type="button" onClick={() => setShowLinkForm((value) => !value)}>링크 추가</button></div></div>
        {evidenceFile && <div className="experience-link-form"><p className="muted">선택한 파일: {evidenceFile.name} · {formatSize(evidenceFile.size)}</p><label>증빙 제목<input maxLength={240} value={evidenceTitle} onChange={(event) => setEvidenceTitle(event.target.value)} /></label><label>설명<textarea rows={2} maxLength={3000} value={evidenceDescription} onChange={(event) => setEvidenceDescription(event.target.value)} placeholder="이 증빙이 무엇을 보여주는지 적어보세요." /></label><button className="primary" type="button" disabled={busy === "upload" || !evidenceTitle.trim()} onClick={() => void addFile()}>{busy === "upload" ? "업로드 중…" : "증빙 업로드"}</button></div>}
        {showLinkForm && <div className="experience-link-form"><label>링크 제목<input maxLength={240} value={linkTitle} onChange={(e) => setLinkTitle(e.target.value)} placeholder="예: 발표 영상" /></label><label>URL<input type="url" inputMode="url" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://" /></label><label>설명<textarea rows={2} maxLength={3000} value={linkDescription} onChange={(e) => setLinkDescription(e.target.value)} /></label><button className="primary" type="button" disabled={busy === "link"} onClick={() => void addLink()}>{busy === "link" ? "저장 중…" : "링크 저장"}</button></div>}
        {detail.evidence.length ? <div className="experience-evidence-grid">{detail.evidence.map((evidence) => <article className="experience-evidence-card" key={evidence.evidenceId}>
          <div className="experience-evidence-icon" aria-hidden="true">{evidence.type === "IMAGE" ? "▧" : evidence.type === "VIDEO" ? "▷" : "▤"}</div>
          <div className="experience-evidence-copy"><strong>{evidence.title}</strong><span>{evidence.type === "IMAGE" ? "이미지" : evidence.type === "VIDEO" ? "영상 링크" : evidence.externalUrl ? "웹 링크" : "문서"} · {evidenceStatus(evidence.status)}</span>{evidence.narrative && <p>{evidence.narrative}</p>}
            {evidence.attachments.map((attachment) => <button type="button" className="evidence-open-link" key={attachment.id} onClick={() => void openAttachment(attachment)}>{attachment.fileName} · {formatSize(attachment.byteSize)} 열기</button>)}
            {evidence.externalUrl && <a className="evidence-open-link" href={evidence.externalUrl} target="_blank" rel="noreferrer">링크 열기 ↗</a>}
            {(["DRAFT", "REJECTED"].includes(evidence.status)) && <button className="quiet-button" type="button" onClick={() => startEvidenceEdit(evidence)}>증빙 제목·설명 수정</button>}
          </div>
          {editingEvidenceId === evidence.evidenceId && <div className="experience-evidence-edit"><label>증빙 제목<input maxLength={240} value={editingEvidenceTitle} onChange={(event) => setEditingEvidenceTitle(event.target.value)} /></label><label>설명<textarea rows={3} maxLength={3000} value={editingEvidenceDescription} onChange={(event) => setEditingEvidenceDescription(event.target.value)} /></label><div><button className="secondary" type="button" onClick={() => setEditingEvidenceId(null)}>취소</button><button className="primary" type="button" disabled={busy === `evidence-edit:${evidence.evidenceId}` || !editingEvidenceTitle.trim()} onClick={() => void saveEvidenceEdit(evidence)}>{busy === `evidence-edit:${evidence.evidenceId}` ? "저장 중…" : "수정 저장"}</button></div></div>}
          {(["DRAFT", "REJECTED"].includes(evidence.status)) && <button className="quiet-button evidence-submit" type="button" disabled={busy === `evidence:${evidence.evidenceId}`} onClick={() => void submitEvidence(evidence)}>교사 확인 요청</button>}
        </article>)}</div> : <p className="experience-empty-inline">아직 추가한 증빙이 없습니다.</p>}
        <p className="experience-upload-hint">JPEG, PNG, WebP, PDF, Word, PowerPoint, Excel · 파일당 최대 10MB</p>
      </section>

      {(detail.competencies.length > 0 || detail.achievements.length > 0 || detail.lifeStamps.length > 0) && <section className="experience-growth-connections">
        {detail.competencies.length > 0 && <ConnectionGroup title="확인된 역량" items={detail.competencies.map((item) => ({ id: item.competencyId, name: item.name, detail: item.indicator || `평가 기록 · ${dateText(item.observedAt)}` }))} />}
        {detail.achievements.length > 0 && <ConnectionGroup title="성취" items={detail.achievements.map((item) => ({ id: item.achievementId, name: item.name, detail: item.description || `교사 확인 · ${dateText(item.confirmedAt)}` }))} />}
        {detail.lifeStamps.length > 0 && <ConnectionGroup title="Life Stamp" items={detail.lifeStamps.map((item) => ({ id: item.credentialId, name: item.credentialName, detail: `${item.issuerName} · ${dateText(item.issuedAt)}` }))} />}
      </section>}
    </section>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return <div className="experience-fact"><span>{label}</span><strong>{value}</strong></div>;
}

function ConnectionGroup({ title, items }: { title: string; items: Array<{ id: string; name: string; detail: string }> }) {
  return <section className="experience-connection-group"><h2>{title}</h2>{items.map((item) => <article key={item.id}><strong>{item.name}</strong><span>{item.detail}</span></article>)}</section>;
}

function evidenceStatus(value: string) {
  const labels: Record<string, string> = { DRAFT: "내 기록", SUBMITTED: "확인 요청 중", VERIFIED: "교사 확인 완료", REJECTED: "보완 필요" };
  return labels[value] || value;
}

function formatSize(value: number) {
  return value < 1024 * 1024 ? `${Math.max(1, Math.round(value / 1024))}KB` : `${(value / (1024 * 1024)).toFixed(1)}MB`;
}
